import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';

import { apiGet } from '../Api';
import PublicShell from './PublicShell';
import './PublicDocument.css';
import './Wiki.css';

function normalizeBlock(block) {
    return {
        type: String(block?.type ?? block?.Type ?? 'text'),
        text: String(block?.text ?? block?.Text ?? ''),
        targetSlug: String(block?.targetSlug ?? block?.TargetSlug ?? ''),
    };
}

function normalizeSummary(page) {
    return {
        slug: String(page?.slug ?? page?.Slug ?? ''),
        title: String(page?.title ?? page?.Title ?? ''),
    };
}

function normalizePage(page) {
    return {
        ...normalizeSummary(page),
        blocks: (page?.blocks ?? page?.Blocks ?? []).map(normalizeBlock),
    };
}

export default function Wiki() {
    const { slug = '' } = useParams();
    const [pages, setPages] = useState([]);
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notFound, setNotFound] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        setNotFound(false);
        if (slug) setPage(null);
        try {
            const path = slug
                ? `/wiki/${encodeURIComponent(slug)}`
                : '/wiki';
            const response = await apiGet(path, false);
            const result = await response.json();
            if (slug) {
                setPage(normalizePage(result));
            } else {
                setPages(Array.isArray(result)
                    ? result.map(normalizeSummary)
                    : []);
            }
        } catch (loadError) {
            if (loadError?.status === 404) {
                setNotFound(true);
            } else {
                setError('The wiki could not be loaded. Check your connection and try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        document.title = page?.title
            ? `${page.title} | Progress Lands Wiki`
            : 'Progress Lands Wiki';
    }, [page?.title]);

    const title = page?.title || 'Progress Lands Wiki';

    return (
        <PublicShell document>
            <article className="public-document wiki" aria-labelledby="wiki-title">
                <header className="public-document__header">
                    <p className="public-document__eyebrow">Field manual</p>
                    {slug && (
                        <nav aria-label="Breadcrumb" className="wiki__breadcrumb">
                            <Link to="/wiki">Wiki</Link>
                        </nav>
                    )}
                    <h1 id="wiki-title">{title}</h1>
                    {!slug && (
                        <p className="public-document__intro">
                            Actions, survival systems, and hard-earned wasteland knowledge.
                        </p>
                    )}
                </header>

                {loading && (
                    <section className="public-document__state" aria-live="polite" aria-busy="true">
                        <h2>Loading field notes</h2>
                        <p>Checking the latest survival advice…</p>
                    </section>
                )}

                {!loading && error && (
                    <section className="public-document__state" role="alert">
                        <h2>Signal lost</h2>
                        <p>{error}</p>
                        <button className="public-document__button" type="button" onClick={load}>
                            Try again
                        </button>
                    </section>
                )}

                {!loading && notFound && (
                    <section className="public-document__state">
                        <h2>Wiki page not found</h2>
                        <p>The requested field note does not exist or is not published.</p>
                        <Link className="wiki__return-link" to="/wiki">Return to the wiki</Link>
                    </section>
                )}

                {!loading && !error && !notFound && !slug && pages.length === 0 && (
                    <section className="public-document__state">
                        <h2>No field notes yet</h2>
                        <p>No wiki pages are currently published.</p>
                    </section>
                )}

                {!loading && !error && !notFound && !slug && pages.length > 0 && (
                    <section className="public-document__section">
                        <h2>Wiki pages</h2>
                        <ul className="wiki__page-list">
                            {pages.map(item => (
                                <li key={item.slug}>
                                    <Link to={`/wiki/${item.slug}`}>{item.title}</Link>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {!loading && !error && !notFound && slug && page && (
                    <section className="public-document__section wiki__body">
                        {page.blocks.map((block, index) => {
                            const key = `${block.type}-${index}`;
                            if (block.type === 'h2') {
                                return <h2 key={key}>{block.text}</h2>;
                            }
                            if (block.type === 'page-link') {
                                return (
                                    <p key={key}>
                                        <Link to={`/wiki/${block.targetSlug}`}>{block.text}</Link>
                                    </p>
                                );
                            }
                            return <p key={key}>{block.text}</p>;
                        })}
                    </section>
                )}
            </article>
        </PublicShell>
    );
}
